import React from 'react';
import Icon from '../../../components/AppIcon';

const QualityAssurance = () => {
  const qaMethodologies = [
    {
      icon: "TestTube2",
      title: "Automated Testing",
      description: "Comprehensive test suites covering unit tests, integration tests, and end-to-end scenarios ensuring code reliability.",
      coverage: "85%+ code coverage",
      tools: ["Jest", "Cypress", "Playwright"]
    },
    {
      icon: "Eye",
      title: "Manual QA Review",
      description: "Human-driven exploratory testing identifying edge cases and user experience issues automated tests might miss.",
      coverage: "100% feature validation",
      tools: ["TestRail", "Jira", "BrowserStack"]
    },
    {
      icon: "Shield",
      title: "Security Auditing",
      description: "Vulnerability scanning, penetration testing, and security best practices implementation protecting your application.",
      coverage: "OWASP Top 10 compliance",
      tools: ["Snyk", "SonarQube", "OWASP ZAP"]
    },
    {
      icon: "Gauge",
      title: "Performance Testing",
      description: "Load testing, stress testing, and performance profiling ensuring your application scales under real-world conditions.",
      coverage: "Sub-3s load times",
      tools: ["Lighthouse", "WebPageTest", "K6"]
    },
    {
      icon: "Accessibility",
      title: "Accessibility Compliance",
      description: "WCAG 2.1 AA standard adherence ensuring your application is usable by everyone including assistive technology users.",
      coverage: "WCAG 2.1 AA certified",
      tools: ["axe DevTools", "WAVE", "NVDA"]
    },
    {
      icon: "Smartphone",
      title: "Cross-Platform Testing",
      description: "Validation across multiple browsers, devices, and operating systems ensuring consistent user experience everywhere.",
      coverage: "10+ device configurations",
      tools: ["BrowserStack", "LambdaTest", "Sauce Labs"]
    }
  ];

  const riskMitigation = [
    {
      risk: "Scope Creep",
      strategy: "Strict change request process with impact analysis and client approval before implementation.",
      icon: "AlertTriangle"
    },
    {
      risk: "Technical Debt",
      strategy: "Regular code refactoring sprints and architectural reviews maintaining long-term code health.",
      icon: "Code2"
    },
    {
      risk: "Timeline Delays",
      strategy: "Buffer time allocation, early risk identification, and proactive stakeholder communication.",
      icon: "Clock"
    },
    {
      risk: "Integration Failures",
      strategy: "Continuous integration pipelines with automated deployment testing catching issues early.",
      icon: "GitBranch"
    }
  ];

  return (
    <div className="space-y-6 md:space-y-8 lg:space-y-10">
      <div className="bg-card border border-border rounded-lg p-6 md:p-8 lg:p-10">
        <div className="mb-6 md:mb-8">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold text-foreground mb-3 md:mb-4">
            Quality Assurance Methodology
          </h3>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
            Multi-layered testing approach ensuring your application meets the highest standards of reliability, security, and performance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {qaMethodologies?.map((methodology, index) => (
            <div 
              key={index}
              className="bg-background border border-border rounded-lg p-4 md:p-6 hover:border-primary transition-all duration-300"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-success/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <Icon name={methodology?.icon} size={24} color="var(--color-success)" />
              </div>
              <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">
                {methodology?.title}
              </h4>
              <p className="text-sm md:text-base text-muted-foreground mb-3 md:mb-4 leading-relaxed">
                {methodology?.description}
              </p>
              <div className="flex items-center gap-2 text-xs md:text-sm text-success font-medium mb-3">
                <Icon name="CheckCircle2" size={16} />
                <span>{methodology?.coverage}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {methodology?.tools?.map((tool, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-1 bg-muted text-foreground text-xs rounded"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-6 md:p-8 lg:p-10">
        <div className="mb-6 md:mb-8">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold text-foreground mb-3 md:mb-4">
            Risk Mitigation Strategies
          </h3>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
            Proactive approaches to common project risks ensuring smooth delivery and minimizing unexpected challenges.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {riskMitigation?.map((item, index) => (
            <div 
              key={index}
              className="bg-background border border-border rounded-lg p-4 md:p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={item?.icon} size={20} color="var(--color-warning)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">
                    {item?.risk}
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {item?.strategy}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QualityAssurance;